import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext, useRef } from "react";
import {
  ChoosePortraitRequest,
  CreatePortraitUploadResponse,
  FlagPortraitRequest,
  GetPortraitResponse,
  PortraitJobStatus,
  RequestChristmasImageRequest,
} from "tng-address-collection-shared/types/portraitsApiTypes.ts";
import { useAuthentication } from "../../../src/auth/useAuthentication.ts";
import { callPortraitApi } from "./portraitApi";
import { PortraitSubjectContext } from "./portraitSubject";

const SECOND = 1_000;
const MINUTE = 60 * SECOND;

/** Signed image links last ten minutes at least, see portraitResponse.ts */
export const IMAGE_LINK_RENEWAL_INTERVAL = 9 * MINUTE;

export const portraitQueryKey = (apiPath: string) =>
  ["portrait", apiPath] as const;

export const pollIntervalAfter = (waitedMilliseconds: number) => {
  if (waitedMilliseconds < MINUTE) {
    return 3 * SECOND;
  }
  if (waitedMilliseconds < 6 * MINUTE) {
    return 10 * SECOND;
  }
  return 30 * SECOND;
};

export const isUnderWay = (job: { status: PortraitJobStatus } | undefined) =>
  job?.status === "queued" || job?.status === "processing";

export const isAnyJobUnderWay = (portrait: GetPortraitResponse | undefined) =>
  isUnderWay(portrait?.cutout) || isUnderWay(portrait?.christmasImage);

export const usePortrait = () => {
  const { getToken, isAuthenticated } = useAuthentication();
  const { apiPath } = useContext(PortraitSubjectContext);
  const waitingSince = useRef<number>(undefined);

  return useQuery({
    queryKey: portraitQueryKey(apiPath),
    queryFn: async ({ signal }) =>
      callPortraitApi(apiPath, GetPortraitResponse, {
        token: await getToken(),
        signal,
      }),
    enabled: isAuthenticated,
    refetchInterval: ({ state }) => {
      if (!isAnyJobUnderWay(state.data)) {
        waitingSince.current = undefined;
        return IMAGE_LINK_RENEWAL_INTERVAL;
      }
      waitingSince.current ??= Date.now();
      return pollIntervalAfter(Date.now() - waitingSince.current);
    },
  });
};

const uploadToStorage = async (
  { url, fields }: CreatePortraitUploadResponse,
  photo: File,
) => {
  const form = new FormData();
  for (const [name, value] of Object.entries(fields)) {
    form.append(name, value);
  }
  // S3 ignores every field after the file
  form.append("file", photo);

  const response = await fetch(url, { method: "POST", body: form });
  if (!response.ok) {
    throw new Error(`Storage returned status ${response.status}`);
  }
};

/** Every change answers with the portrait as it is now */
const usePortraitChange = <T>(
  change: (
    variables: T,
    token: string | undefined,
    apiPath: string,
  ) => Promise<unknown>,
) => {
  const { getToken } = useAuthentication();
  const { apiPath } = useContext(PortraitSubjectContext);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: T) =>
      GetPortraitResponse.parse(
        await change(variables, await getToken(), apiPath),
      ),
    // A fetch still under way would overwrite the answer with an older state
    onMutate: () =>
      queryClient.cancelQueries({ queryKey: portraitQueryKey(apiPath) }),
    onSuccess: (portrait) =>
      queryClient.setQueryData(portraitQueryKey(apiPath), portrait),
  });
};

export const useUploadPortrait = () => {
  const { getToken } = useAuthentication();

  return usePortraitChange(async (photo: File, token, apiPath) => {
    const upload = await callPortraitApi(
      `${apiPath}/uploads`,
      CreatePortraitUploadResponse,
      { token, body: { contentType: photo.type } },
    );
    await uploadToStorage(upload, photo);
    return callPortraitApi(`${apiPath}/cutout`, GetPortraitResponse, {
      // The upload may have outlasted the token
      token: await getToken(),
      body: { uploadKey: upload.uploadKey },
    });
  });
};

export const useCutOutAgain = () =>
  usePortraitChange((_: void, token, apiPath) =>
    callPortraitApi(`${apiPath}/cutout`, GetPortraitResponse, {
      token,
      body: {},
    }),
  );

export const useRequestChristmasImage = () =>
  usePortraitChange((options: RequestChristmasImageRequest, token, apiPath) =>
    callPortraitApi(`${apiPath}/christmas-image`, GetPortraitResponse, {
      token,
      body: options,
    }),
  );

export const useChoosePortrait = () =>
  usePortraitChange((choice: ChoosePortraitRequest, token, apiPath) =>
    callPortraitApi(`${apiPath}/choice`, GetPortraitResponse, {
      token,
      body: choice,
    }),
  );

export const useFlagPortrait = () =>
  usePortraitChange((request: FlagPortraitRequest, token, apiPath) =>
    callPortraitApi(`${apiPath}/flag`, GetPortraitResponse, {
      token,
      body: request,
    }),
  );
